/**
 * Where a client on a self-paced plan stands — the R16 signals the trainer reads INSTEAD of an
 * adherence figure, on the roster and on the client detail alike.
 *
 * ── Why there is no percentage to fall back on ─────────────────────────────────────────────────
 * R15 forbids a plan-relative adherence figure for this mode, and the arithmetic is why rather than
 * the rule: adherence is completed over completed plus no-show plus due-but-not-completed, and a
 * self-paced plan has no due-but-not-completed occurrence by construction. The denominator
 * collapses and the figure reads 100 % for a client who trains once a month. So the backend sends
 * `pct: null` and this shape in its place, and `null` here must never be rendered as `0 %`, as an
 * empty ring, or as anything else that looks like a measurement.
 *
 * ── One module, two surfaces, on purpose ───────────────────────────────────────────────────────
 * `ClientPlanStandingResponse` rides on BOTH the roster row (`GetCustomerResponse.planStanding`)
 * and the client-detail compliance read, and the backend's own note says why: "it is the same
 * answer to the same question and a trainer must not see it two ways". Two readers of one wire
 * shape is how the roster and the detail learn to disagree about how long somebody has been
 * waiting, so both call `clientStanding` and neither reads the wire itself.
 *
 * Pure: no React, no DOM, no query hook, no i18next. What leaves here is decided FACTS and a
 * discriminated recency; the words are the caller's.
 */

import type { TrainWithMeModulesClientsDtosResponsesClientPlanStandingResponse as PlanStandingWire } from '~/shared/api/generated/model';

/** Where the cursor stands, as cycle position. Both 1-based; `total` is the plan's cycle count. */
export interface StandingPosition {
  readonly cycleIndex: number;
  readonly cycleTotal: number;
}

/**
 * Occurrences finished and declined in the recent window, kept as two numbers.
 *
 * They are shown BESIDE each other and are never divided into one another. R16 forbids a
 * completed-to-skipped ratio as a headline: it reads as a perfect score for the client who simply
 * stopped without declining anything, which rewards the least engaged client and penalises the one
 * who says they are sitting a session out. No consumer of this shape may compute a percentage from
 * it — that is the whole reason it is two fields and not a rate.
 */
export interface StandingBalance {
  readonly completed: number;
  readonly skipped: number;
}

/**
 * How long ago the client last trained — the signal R16 calls load-bearing, and the one a surface
 * with room for only one figure must show.
 *
 * The three cases are genuinely different states and must not render the same, which is why this is
 * a union rather than a nullable number:
 *
 * - `trained` — they have finished an occurrence on this plan, `days` ago.
 * - `neverTrained` — they have lived occurrences but finished none, so every one of them was
 *   declined. There is no recency to state and saying "0 days" would invert the truth.
 * - `awaitingFirst` — nothing has happened at all. A plan waiting since it was assigned is not a
 *   client who stopped, and the honest sentence is that the plan waits.
 */
export type StandingRecency =
  | { readonly kind: 'trained'; readonly days: number }
  | { readonly kind: 'neverTrained' }
  | { readonly kind: 'awaitingFirst' };

export interface ClientStanding {
  readonly position: StandingPosition;
  readonly recency: StandingRecency;
  /**
   * How many days the cursor has stood on its current occurrence, or `null` where the figure would
   * add nothing.
   *
   * It is NOT the same question as `recency`, and the two diverge exactly when somebody declines:
   * a client who declines a session after three silent weeks is standing on a one-day-old cursor
   * and has still not trained for three weeks. Both sentences are true, so the second one is
   * carried — but only where it says something the first does not. It is dropped in two cases:
   * when it equals the recency figure, because that is one number printed twice; and when it is
   * zero, because "waiting 0 days" is not a wait at all — it says something happened today, which
   * every recency line already says better.
   */
  readonly waitingDays: number | null;
  readonly balance: StandingBalance;
}

/** A wire count, or `null` when the field is not a number this build can read. */
function numberOrNull(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** A wire count that the contract declares non-nullable. Absent reads as zero, which is true of it. */
function countOf(value: number | null | undefined): number {
  return numberOrNull(value) ?? 0;
}

/**
 * The client's standing, or `null` when the wire carried none — which is the ordinary answer for
 * every client whose plan is dated, and the signal to render the adherence figure instead.
 *
 * `null` is also returned for a standing whose POSITION cannot be read. `cycleIndex` and
 * `cycleTotal` are non-nullable on the contract, so a missing one means a build reading a wire it
 * does not understand, and "cykl 0 z 0" is worse than the em dash the caller already draws for
 * absent data. The counts are not treated that way: zero completions is a real and readable answer.
 */
export function clientStanding(wire: PlanStandingWire | null | undefined): ClientStanding | null {
  if (wire == null) return null;

  const cycleIndex = numberOrNull(wire.cycleIndex);
  const cycleTotal = numberOrNull(wire.cycleTotal);
  if (cycleIndex === null || cycleTotal === null) return null;

  const daysSinceLastTraining = numberOrNull(wire.daysSinceLastTraining);
  const waitingDays = numberOrNull(wire.waitingDays);
  // Lived means DECIDED — executed or declined. It is what separates "this plan has never been
  // touched" from "this client has only ever declined", and it is read here rather than inferred
  // from a null `waitingDays`: the two coincide today, and a backend that starts counting a fresh
  // plan's wait would silently turn every waiting plan into a client who stopped.
  const livedCount = countOf(wire.livedCount);

  const recency: StandingRecency =
    daysSinceLastTraining !== null
      ? { kind: 'trained', days: daysSinceLastTraining }
      : livedCount > 0
        ? { kind: 'neverTrained' }
        : { kind: 'awaitingFirst' };

  return {
    position: { cycleIndex, cycleTotal },
    recency,
    // A zero wait is dropped everywhere. It was drawn as "Czeka 0 dni" under "Plan czeka na
    // pierwszy trening" on every plan assigned today — a line that repeats the one above it and
    // measures nothing. Above zero, it is dropped only where it repeats the recency figure; in the
    // two branches with no recency figure it is the only elapsed time the trainer can be given.
    waitingDays:
      waitingDays === null ||
      waitingDays === 0 ||
      (recency.kind === 'trained' && waitingDays === recency.days)
        ? null
        : waitingDays,
    balance: {
      completed: countOf(wire.completedInWindow),
      skipped: countOf(wire.skippedInWindow),
    },
  };
}
